import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { getMusterRoll } from '../../services/api/implementation/impl';
import { IndianRupee, LogIn, LogOut, Users, X, Info } from 'lucide-react';
import {
  Tile,
  Callout
} from '../../../shared/components/ui/primitives';
import { formatHours } from './formatHours';

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, '0')}-${`${d.getDate()}`.padStart(2, '0')}`;
};

/**
 * Review a site's muster for a date.
 *
 * Capture still happens on the app. This is the back-office view of that day —
 * who was punched, who was marked absent, who was never marked.
 */
const money = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  });
};

const MusterRoll = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const { projectsForDropdown } = useProjectsList();
  const [projectId, setProjectId] = useState('');
  const [date, setDate] = useState(today);

  const { data, isFetching } = useQuery({
    queryKey: ['musterRoll', projectId, date],
    queryFn: () => getMusterRoll(projectId, date),
    enabled: Boolean(projectId && date)
  });

  const roster = data?.data?.roster ?? data?.data ?? [];
  const rows = Array.isArray(roster) ? roster : [];

  const summary = useMemo(() => {
    const punchedIn = rows.filter((r) => r.punched_in).length;
    const punchedOut = rows.filter((r) => r.punched_out).length;
    const absent = rows.filter((r) => r.status === 'ABSENT').length;
    return { total: rows.length, punchedIn, punchedOut, absent };
  }, [rows]);

  // The site's running total for the day, as the endpoint priced it. Read
  // rather than re-summed here so the tile cannot drift from the rows.
  const earnedToday = data?.data?.earned_today ?? 0;

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Muster roll' }} />
      <div className="p-4 space-y-4">
        <Callout tone="info" collapsible icon={<Info size={14} />} title="About the muster roll" className="sm:max-w-2xl">
          Supervisors mark attendance on the app. Use this page to review any site and date.
        </Callout>

        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            <span className="block text-xs font-medium text-gray-600 mb-1">Project</span>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white min-w-[220px]"
            >
              <option value="">Select a project</option>
              {(projectsForDropdown ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-xs font-medium text-gray-600 mb-1">Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </label>
        </div>

        {projectId && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Tile variant="card" label="On roster" value={summary.total}
            icon={<Users size={18} />}
          />
            <Tile variant="card" label="Punched in" value={summary.punchedIn}
            icon={<LogIn size={18} />}
          />
            <Tile variant="card" label="Punched out" value={summary.punchedOut}
            icon={<LogOut size={18} />}
          />
            <Tile variant="card" label="Absent" value={summary.absent}
            icon={<X size={18} />}
          />
            {/* "so far" only reads correctly on today. This screen picks a
                date, and on a day three weeks back nothing is still running. */}
            <Tile variant="card" label="Spend for the day" value={money(earnedToday)}
            icon={<IndianRupee size={18} />}
          />
          </div>
        )}

        {!projectId ? (
          <div className="bg-white border border-gray-200 rounded-lg py-10 text-center text-gray-500">
            Choose a project to load the muster.
          </div>
        ) : isFetching ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg py-10 text-center text-gray-500">
            No workmen on this site for that date.
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <Th>Workman</Th>
                  <Th>Status</Th>
                  <Th>In</Th>
                  <Th>Out</Th>
                  <Th>Hours</Th>
                  <Th>Earned</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.worker_id ?? row.id} className="border-t border-gray-100">
                    <Td>
                      <div className="font-medium text-gray-900">{row.name}</div>
                      {row.current_task?.name ? (
                        <div className="text-xs text-gray-500">{row.current_task.name}</div>
                      ) : null}
                    </Td>
                    <Td>{statusLabel(row)}</Td>
                    <Td>{formatClock(row.check_in)}</Td>
                    <Td>{outLabel(row)}</Td>
                    <Td>
                      {hoursLabel(row)}
                      {Number(row.break_minutes) > 0 ? (
                        <div className="text-xs text-gray-500">{row.break_minutes}m break</div>
                      ) : null}
                    </Td>
                    <Td>
                      <span className="tabular-nums">{money(row.earned_today)}</span>
                      {row.is_working ? (
                        <div className="text-xs text-gray-500">still running</div>
                      ) : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

const Th = ({ children }) => (
  <th className="text-left font-medium px-4 py-2">{children}</th>
);
const Td = ({ children }) => <td className="px-4 py-2">{children}</td>;

/** Muster times are Indian site time, same as the app punch sheet. */
function formatClock(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata'
  });
}

function outLabel(row) {
  if (row.on_break) return 'On break';
  if (row.is_working) return 'Working now';
  return formatClock(row.check_out);
}

function hoursLabel(row) {
  const n = Number(row.worked_hours ?? row.working_hours ?? row.workingHours);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return formatHours(n);
}

function statusLabel(row) {
  if (row.on_break) return 'On break';
  if (row.is_working) return 'Working now';
  const raw = row.status ?? (row.punched_in ? 'PRESENT' : null);
  if (!raw) return '—';
  return raw
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default MusterRoll;
